import React, { useMemo, useState } from "react";
import { ArrowLeft, Trash2, Tag, Truck } from "lucide-react";
import Layout from "../components/layout";
import { Link } from "react-router-dom";

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-black/10 bg-white shadow-sm ${className}`}
  >
    {children}
  </div>
);

const PrimaryButton = ({ children, className = "", ...props }) => (
  <Link
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </Link>
);

const GhostButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-semibold text-black/80 shadow-sm transition hover:bg-black/5 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const QtyButton = ({ children, ...props }) => (
  <button
    className="grid h-9 w-9 place-items-center rounded-xl border border-black/10 bg-white text-sm font-black text-black/75 shadow-sm transition hover:bg-black/5 active:scale-[0.98]"
    {...props}
  >
    {children}
  </button>
);

const money = (n) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function CartPage() {
  const initialItems = JSON.parse(localStorage.getItem("cart")) || [];

  const [items, setItems] = useState(initialItems);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + it.price * it.quantity, 0),
    [items]
  );

  // simple rules (change as you want)
  const shipping = subtotal === 0 ? 0 : subtotal >= 80 ? 0 : 6.99;
  const discount =
    appliedCoupon?.type === "percent"
      ? (subtotal * appliedCoupon.value) / 100
      : 0;

  const total = Math.max(0, subtotal + shipping - discount);

  const updateQty = (sku, delta) => {
    setItems((prev) =>
      prev
        .map((it) =>
          it.sku === sku
            ? { ...it, quantity: Math.max(1, it.quantity + delta) }
            : it
        )
        .filter(Boolean)
    );

    // Also update localStorage
    const updatedCart = items
      .map((it) =>
        it.sku === sku
          ? { ...it, quantity: Math.max(1, it.quantity + delta) }
          : it
      )
      .filter(Boolean);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    // Dispatch event to notify other components (like Navbar)
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const removeItem = (sku) => {
    setItems((prev) => prev.filter((it) => it.sku !== sku));

    // Also update localStorage
    const updatedCart = items.filter((it) => it.sku !== sku);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    // Dispatch event to notify other components (like Navbar)
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const applyCoupon = () => {
    const code = coupon.trim().toUpperCase();

    // Example coupons
    // RETRO10 => 10% off
    // FREESHIP => shipping becomes 0 (handled by setting coupon but you can override shipping logic if you want)
    if (code === "RETRO10") {
      setAppliedCoupon({ code, type: "percent", value: 10 });
    } else if (code === "FREESHIP") {
      setAppliedCoupon({ code, type: "freeship", value: 0 });
    } else {
      setAppliedCoupon({ code, type: "invalid", value: 0 });
    }
  };

  const effectiveShipping = appliedCoupon?.type === "freeship" ? 0 : shipping;
  const effectiveDiscount = appliedCoupon?.type === "percent" ? discount : 0;
  const effectiveTotal = Math.max(
    0,
    subtotal + effectiveShipping - effectiveDiscount
  );

  return (
    <Layout>
      <div className="min-h-screen bg-[#fbf7f1] text-black/90">
        {/* Page header */}
        <header className="border-b border-black/10 bg-[#fbf7f1]/80 backdrop-blur">
          <Container>
            <div className="flex flex-col gap-3 py-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-black/60 hover:text-black"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Continue shopping
                </Link>
                <h1 className="mt-2 text-2xl font-black tracking-tight text-black/85 sm:text-3xl">
                  Your Cart
                </h1>
                <p className="mt-1 text-sm font-medium text-black/55">
                  Review your items and checkout when you’re ready.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="rounded-full border border-black/10 bg-white text-center px-3 py-1 text-xs font-bold text-black/60">
                  {items.length} items
                </span>
                <span className="rounded-full border border-black/10 bg-white text-center px-3 py-1 text-xs font-bold text-orange-700">
                  Total: ৳{money(effectiveTotal)}
                </span>
              </div>
            </div>
          </Container>
        </header>

        <main className="py-10">
          <Container>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6 min-w-0">
              {/* Left: items */}
              <div className="lg:col-span-2">
                <Card className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-extrabold text-black/80">
                        Cart Items
                      </div>
                      <div className="mt-1 text-xs font-medium text-black/50">
                        Update quantities or remove items.
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 space-y-4">
                    {items.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-black/15 bg-black/[0.02] p-8 text-center">
                        <div className="text-sm font-extrabold text-black/70">
                          Your cart is empty
                        </div>
                        <div className="mt-1 text-xs font-medium text-black/50">
                          Add some products to get started.
                        </div>
                        <PrimaryButton to="/products" className="mt-4">
                          Browse Products
                        </PrimaryButton>
                      </div>
                    ) : (
                      items.map((it) => (
                        <div
                          key={it.id}
                          className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm"
                        >
                          {/* top row: image + info */}
                          <div className="flex gap-4">
                            <div className="h-20 w-24 shrink-0 overflow-hidden rounded-2xl bg-black/5">
                              <img
                                src={it.img}
                                alt={it.title}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] font-bold uppercase tracking-wide text-black/45">
                                {it.sku} ({it?.variant})
                              </div>

                              <div className="mt-1 line-clamp-2 text-sm font-extrabold text-black/80">
                                {it.title}
                              </div>

                              <div className="mt-2 text-sm font-extrabold text-orange-600">
                                ৳{money(it.price)}
                              </div>
                            </div>
                          </div>

                          {/* bottom row: qty + totals + remove */}
                          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            {/* qty controls */}
                            <div className="flex items-center justify-between gap-3 sm:justify-start">
                              <div className="text-xs font-bold text-black/55 sm:hidden">
                                Quantity
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  className="grid h-10 w-10 place-items-center rounded-xl border border-black/10 bg-white text-sm font-black text-black/75 shadow-sm transition hover:bg-black/5 active:scale-[0.98]"
                                  onClick={() => updateQty(it.sku, -1)}
                                >
                                  -
                                </button>

                                <div className="min-w-[44px] text-center text-sm font-extrabold text-black/75">
                                  {it.quantity}
                                </div>

                                <button
                                  className="grid h-10 w-10 place-items-center rounded-xl border border-black/10 bg-white text-sm font-black text-black/75 shadow-sm transition hover:bg-black/5 active:scale-[0.98]"
                                  onClick={() => updateQty(it.sku, +1)}
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            {/* line total + remove */}
                            <div className="flex items-center justify-between gap-3 sm:justify-end">
                              <div className="text-xs font-bold text-black/55 sm:hidden">
                                Total
                              </div>

                              <div className="text-sm font-extrabold text-black/75">
                                ৳{money(it.price * it.quantity)}
                              </div>

                              <button
                                onClick={() => removeItem(it.sku)}
                                className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-3 py-2 text-xs font-bold text-black/60 shadow-sm transition hover:bg-black/5"
                              >
                                <Trash2 className="h-4 w-4" />
                                <p className="lg:inline sm:hidden">Remove</p>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Coupon */}
                  {/* <div className="mt-6 rounded-2xl border border-black/10 bg-black/[0.02] p-4 sm:p-5">
                    <div className="flex items-center gap-2 text-sm font-extrabold text-black/75">
                      <Tag className="h-4 w-4" />
                      Have a coupon?
                    </div>

                    <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                      <input
                        value={coupon}
                        onChange={(e) => setCoupon(e.target.value)}
                        placeholder="Enter code (try RETRO10 / FREESHIP)"
                        className="h-11 w-full rounded-xl border border-black/10 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-orange-600/25"
                      />
                      <GhostButton className="h-11" onClick={applyCoupon}>
                        Apply
                      </GhostButton>
                    </div>

                    {appliedCoupon?.type === "percent" && (
                      <div className="mt-2 text-xs font-semibold text-emerald-700">
                        ✅ Coupon applied: {appliedCoupon.code} (
                        {appliedCoupon.value}% off)
                      </div>
                    )}
                    {appliedCoupon?.type === "freeship" && (
                      <div className="mt-2 text-xs font-semibold text-emerald-700">
                        ✅ Coupon applied: {appliedCoupon.code} (Free shipping)
                      </div>
                    )}
                    {appliedCoupon?.type === "invalid" && (
                      <div className="mt-2 text-xs font-semibold text-red-600">
                        ❌ Invalid coupon code
                      </div>
                    )}
                  </div> */}
                </Card>
              </div>

              {/* Right: summary */}
              <div className="lg:col-span-1">
                <Card className="sticky top-24 p-4 sm:p-6">
                  <div className="text-sm font-extrabold text-black/80">
                    Order Summary
                  </div>
                  <div className="mt-1 text-xs font-medium text-black/50">
                    Shipping calculated at checkout.
                  </div>

                  <div className="mt-5 space-y-3">
                    <Row label="Subtotal" value={`৳${money(subtotal)}`} />
                    {/* <Row
                      label={
                        <span className="inline-flex items-center gap-2">
                          <Truck className="h-4 w-4 text-black/50" />
                          Shipping
                        </span>
                      }
                      value={
                        effectiveShipping === 0 && subtotal > 0
                          ? "Free"
                          : `৳${money(effectiveShipping)}`
                      }
                      valueClass="text-black/70"
                    /> */}

                    <Row
                      label="Discount"
                      value={
                        effectiveDiscount > 0
                          ? `- ৳${money(effectiveDiscount)}`
                          : "৳0.00"
                      }
                      valueClass={
                        effectiveDiscount > 0
                          ? "text-emerald-700"
                          : "text-black/70"
                      }
                    />

                    <div className="my-4 h-px bg-black/10" />

                    <Row
                      label={
                        <span className="text-sm font-black text-black/85">
                          Total
                        </span>
                      }
                      value={
                        <span className="text-sm font-black">
                          ৳{money(effectiveTotal)}
                        </span>
                      }
                    />

                    <PrimaryButton
                      disabled={items.length === 0}
                      to="/checkout"
                      className="mt-3 w-full h-11"
                    >
                      Checkout
                    </PrimaryButton>

                    <GhostButton
                      onClick={() => window.history.back()}
                      className="w-full h-11"
                    >
                      Continue Shopping
                    </GhostButton>

                    {/* <div className="mt-3 rounded-2xl border border-black/10 bg-black/[0.02] p-4">
                      <div className="text-xs font-extrabold text-black/75">
                        Free Shipping
                      </div>
                      <div className="mt-1 text-xs font-medium text-black/55">
                        Orders over ৳2000 qualify for free shipping.
                      </div>
                    </div> */}
                  </div>
                </Card>
              </div>
            </div>
          </Container>
        </main>
      </div>
    </Layout>
  );
}

function Row({ label, value, valueClass = "text-black/80" }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold text-black/60">{label}</div>
      <div className={`text-sm font-extrabold ${valueClass}`}>{value}</div>
    </div>
  );
}
